import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import Bugsnag from "@bugsnag/js";
import isEmpty from "lodash/isEmpty";
import { DEFAULT_NOTIFICATION_PAYLOAD, NOTIFICATION_VARIANTS } from "@/utils/applicationConstants";
import { ANALYTIC_EVENTS, ANALYTICS_USER_TYPES, identifyUser, sendAnalytics } from "@/utils/analytics";
import {
  LENDER_PORTAL_LENDING_PROGRAM_REDUCER_PATH,
  LENDING_PROGRAM_DETAIL_SERVICE_TAG,
  LENDING_PROGRAM_RELATED_TAGS,
  LENDING_PROGRAM_SERVICE_TAG,
} from "@/features/LendingProgram/utils/lendingProgramServiceTags";
import { LIST_SERVICE_TAG } from "@/shared/store/service/commonServiceTags";
import { baseUrl, onPrepareHeaders } from "@/redux/services/helpers";
import { showNotification } from "@/redux/actions/notification";
import { getQueryParams } from "@/utils/httpRequest";
import { requestCloseModal } from "@/redux/actions/modal";
import { setLenderProfileData } from "@/redux/reducers/lender_profile";

export const lenderPortalProgramService = createApi({
  reducerPath: LENDER_PORTAL_LENDING_PROGRAM_REDUCER_PATH,
  tagTypes: LENDING_PROGRAM_RELATED_TAGS,
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: onPrepareHeaders,
  }),
  endpoints: builder => ({
    getLendingPrograms: builder.query({
      query: token => `lenders/${token}/lending-programs`,
      providesTags: result =>
        result
          ? [
              ...result.programs.map(({ lendingProgramId }) => ({
                type: LENDING_PROGRAM_SERVICE_TAG,
                id: lendingProgramId,
              })),
              { type: LENDING_PROGRAM_SERVICE_TAG, id: LIST_SERVICE_TAG },
            ]
          : [{ type: LENDING_PROGRAM_SERVICE_TAG, id: LIST_SERVICE_TAG }],
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        try {
          const {
            data: { contact },
          } = await queryFulfilled;

          const { data } = getState().lenderProfileStore;

          identifyUser({
            userId: contact.contactId,
            userType: ANALYTICS_USER_TYPES.LENDER,
            email: contact.email,
            firstName: contact.firstName,
            lastName: contact.lastName,
            fullName: `${contact.firstName} ${contact.lastName}`,
            companyId: contact.companyId,
            companyName: contact.companyName,
          });

          if (isEmpty(data)) dispatch(setLenderProfileData(contact));
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    getLendingProgram: builder.query({
      query: ({ token, lendingProgramId }) => `lenders/${token}/lending-programs/${lendingProgramId}`,
      providesTags: ({ lendingProgramId }) => [{ type: LENDING_PROGRAM_SERVICE_TAG, id: lendingProgramId }],
    }),
    addLendingProgram: builder.mutation({
      query: ({ token, ...body }) => ({
        url: `lenders/${token}/lending-programs`,
        method: "POST",
        body,
      }),
      invalidatesTags: LENDING_PROGRAM_RELATED_TAGS,
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDING_PROGRAM_CREATED, data);

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Lending Program have been created!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(requestCloseModal());
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    updateLendingProgram: builder.mutation({
      query: ({ token, lendingProgramId, ...body }) => ({
        url: `lenders/${token}/lending-programs/${lendingProgramId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: LENDING_PROGRAM_RELATED_TAGS,
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDING_PROGRAM_UPDATED, data);

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Lending Program have been updated!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    deleteLendingProgram: builder.mutation({
      query: ({ token, lendingProgramId }) => ({
        url: `lenders/${token}/lending-programs/${lendingProgramId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: LENDING_PROGRAM_SERVICE_TAG, id: LIST_SERVICE_TAG }],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDING_PROGRAM_DELETED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Lending Program have been deleted!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );

          dispatch(requestCloseModal());
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    getLendingProgramDetails: builder.query({
      query: ({ token, lendingProgramId, parameterType, ...params }) =>
        `lenders/${token}/lending-programs/${lendingProgramId}/types/${parameterType}/parameters?${getQueryParams(
          params,
        )}`,
      providesTags: (result, resultType, { parameterType }) => [
        { type: LENDING_PROGRAM_DETAIL_SERVICE_TAG, id: parameterType },
      ],
    }),
    addLendingProgramDetail: builder.mutation({
      query: ({ token, lendingProgramId, parameterType, parameterId }) => ({
        url: `lenders/${token}/lending-programs/${lendingProgramId}/types/${parameterType}/parameters/${parameterId}`,
        method: "POST",
      }),
      invalidatesTags: () => [
        // { type: LENDING_PROGRAM_DETAIL_SERVICE_TAG, id: parameterType },
        // { type: LENDING_PROGRAM_SERVICE_TAG, id: lendingProgramId },
        { type: LENDING_PROGRAM_SERVICE_TAG, id: LIST_SERVICE_TAG },
      ],
      async onQueryStarted(
        { token, lendingProgramId, parameterType, parameterId },
        { dispatch, queryFulfilled, getState },
      ) {
        // Get all cached queries for this parameter type
        const state = getState();
        const queries = lenderPortalProgramService.util.selectCachedArgsForQuery(state, "getLendingProgramDetails");

        // Find all matching queries and update them optimistically
        const patchResults = [];

        queries.forEach(queryArgs => {
          if (
            queryArgs.token === token &&
            queryArgs.lendingProgramId === lendingProgramId &&
            queryArgs.parameterType === parameterType
          ) {
            const patchResult = dispatch(
              lenderPortalProgramService.util.updateQueryData("getLendingProgramDetails", queryArgs, draft => {
                const parameter = draft.find(p => p.parameterId === parameterId);
                if (parameter) {
                  parameter.isActive = true;
                }
              }),
            );
            patchResults.push(patchResult);
          }
        });

        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDING_PROGRAM_CREATED, data);

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: `${data.parameterName} has been added to the program!`,
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
        } catch (err) {
          // Revert all optimistic updates on error
          patchResults.forEach(patchResult => patchResult.undo());

          Bugsnag.notify(err);
        }
      },
    }),
    addLendingProgramDetails: builder.mutation({
      query: ({ token, lendingProgramId, parameterType, parentId }) => ({
        url: `lenders/${token}/lending-programs/${lendingProgramId}/types/${parameterType}/parameters${
          parentId ? `?parentId=${parentId}` : ""
        }`,
        method: "POST",
      }),
      invalidatesTags: (result, error, { lendingProgramId, parameterType }) => [
        { type: LENDING_PROGRAM_DETAIL_SERVICE_TAG, id: parameterType },
        { type: LENDING_PROGRAM_SERVICE_TAG, id: lendingProgramId },
        { type: LENDING_PROGRAM_SERVICE_TAG, id: LIST_SERVICE_TAG },
      ],
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "All options have been added to the program!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    deleteLendingProgramDetail: builder.mutation({
      query: ({ token, lendingProgramId, parameterType, parameterId }) => ({
        url: `lenders/${token}/lending-programs/${lendingProgramId}/types/${parameterType}/parameters/${parameterId}`,
        method: "DELETE",
      }),
      invalidatesTags: () => [
        // { type: LENDING_PROGRAM_DETAIL_SERVICE_TAG, id: parameterType },
        // { type: LENDING_PROGRAM_SERVICE_TAG, id: lendingProgramId },
        { type: LENDING_PROGRAM_SERVICE_TAG, id: LIST_SERVICE_TAG },
      ],
      async onQueryStarted(
        { token, lendingProgramId, parameterType, parameterId },
        { dispatch, queryFulfilled, getState },
      ) {
        // Get all cached queries for this parameter type
        const state = getState();
        const queries = lenderPortalProgramService.util.selectCachedArgsForQuery(state, "getLendingProgramDetails");

        // Find all matching queries and update them optimistically
        const patchResults = [];

        queries.forEach(queryArgs => {
          if (
            queryArgs.token === token &&
            queryArgs.lendingProgramId === lendingProgramId &&
            queryArgs.parameterType === parameterType
          ) {
            const patchResult = dispatch(
              lenderPortalProgramService.util.updateQueryData("getLendingProgramDetails", queryArgs, draft => {
                const parameter = draft.find(p => p.parameterId === parameterId);
                if (parameter) {
                  parameter.isActive = false;
                }
              }),
            );
            patchResults.push(patchResult);
          }
        });

        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDING_PROGRAM_PARAMETER_REMOVED, data);

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: `${data.parameterName} has been removed from the program!`,
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
        } catch (err) {
          // Revert all optimistic updates on error
          patchResults.forEach(patchResult => patchResult.undo());

          Bugsnag.notify(err);
        }
      },
    }),
    deleteLendingProgramDetails: builder.mutation({
      query: ({ token, lendingProgramId, parameterType, parentId }) => ({
        url: `lenders/${token}/lending-programs/${lendingProgramId}/types/${parameterType}/parameters${
          parentId ? `?parentId=${parentId}` : ""
        }`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { lendingProgramId, parameterType }) => [
        { type: LENDING_PROGRAM_DETAIL_SERVICE_TAG, id: parameterType },
        { type: LENDING_PROGRAM_SERVICE_TAG, id: lendingProgramId },
        { type: LENDING_PROGRAM_SERVICE_TAG, id: LIST_SERVICE_TAG },
      ],
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "All options have been removed from the program!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
  }),
});

export const {
  useGetLendingProgramsQuery,
  useGetLendingProgramQuery,
  useAddLendingProgramMutation,
  useUpdateLendingProgramMutation,
  useDeleteLendingProgramMutation,
  useGetLendingProgramDetailsQuery,
  useAddLendingProgramDetailMutation,
  useAddLendingProgramDetailsMutation,
  useDeleteLendingProgramDetailMutation,
  useDeleteLendingProgramDetailsMutation,
} = lenderPortalProgramService;
